import { useState, type FormEvent } from "react";
import { describeError } from "@/api/client";
import type { Uuid } from "@/api/types";
import {
  Badge,
  Button,
  Checkbox,
  Combobox,
  Dialog,
  DialogClose,
  InlineAlert,
  NumberField,
  RadioGroup,
  Select,
  TextArea,
  TextField,
  useToast,
} from "@/components";
import { formatPercent } from "@/lib/format";
import {
  searchMaterials,
  useImportParsed,
  useImportRecipes,
  useParseFormula,
  type LineInput,
  type LineUnit,
  type MaterialOption,
  type ParseResult,
  type ParsedLine,
} from "../api";
import { UNIT_OPTIONS, dilutionText, materialLabel } from "../display";
import styles from "./FormulaDialogs.module.css";

interface Row {
  line: ParsedLine;
  include: boolean;
  material: MaterialOption | null;
  amount: string | null;
  unit: LineUnit | null;
  /** A line with something to check counts as ready once its material is confirmed or chosen. */
  confirmed: boolean;
}

export interface PasteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported: (result: { formula_id: Uuid; version_id: Uuid; label: string }) => void;
  /** Offer "replace the lines of this draft" (the editor passes its current draft). */
  replaceVersion?: { id: Uuid; name: string } | null;
}

const SAMPLE = "Iso E Super 30\nHedione 20\nAmbroxan 10% in DPG - 5\nVanillin 2";

function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? "" : "s"}`;
}

function recipeAmount(result: ParseResult, line: ParsedLine, column: number): string | null {
  return result.amount_columns.length > 1 ? (line.amounts[column] ?? null) : line.amount;
}

function toRows(result: ParseResult, column: number): Row[] {
  const multi = result.amount_columns.length > 1;
  const unit = result.amount_columns[column]?.unit ?? null;
  return result.lines.map((line) => ({
    line,
    include: line.status !== "unknown" && (multi ? line.amounts.some((value) => value !== null) : !line.absent),
    material: line.match,
    amount: recipeAmount(result, line, column),
    unit: multi ? (unit ?? line.unit) : line.unit,
    confirmed: line.review.length === 0,
  }));
}

/** Paste-a-formula: read text, check only the lines the reader is unsure of, then create a formula (or one per recipe). */
export function PasteDialog({ open, onOpenChange, onImported, replaceVersion }: PasteDialogProps) {
  const parse = useParseFormula();
  const importParsed = useImportParsed();
  const importRecipes = useImportRecipes();
  const toast = useToast();
  const [text, setText] = useState("");
  const [result, setResult] = useState<ParseResult | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [column, setColumn] = useState(0);
  const [scope, setScope] = useState<"one" | "all">("one");
  const [target, setTarget] = useState<"new" | "replace">("new");
  const [name, setName] = useState("");
  const [project, setProject] = useState("");
  const [showReady, setShowReady] = useState(false);
  const [showUnused, setShowUnused] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setText("");
    setResult(null);
    setRows([]);
    setColumn(0);
    setScope("one");
    setTarget("new");
    setName("");
    setProject("");
    setShowReady(false);
    setShowUnused(false);
    setError(null);
    parse.reset();
    importParsed.reset();
    importRecipes.reset();
  }

  function read(event: FormEvent) {
    event.preventDefault();
    if (!text.trim()) {
      setError("Paste a formula first");
      return;
    }
    setError(null);
    parse.mutate(text, {
      onSuccess: (parsed) => {
        const first = parsed.column ?? 0;
        setResult(parsed);
        setColumn(first);
        setRows(toRows(parsed, first));
        const firstLine = text.split("\n").find((value) => value.trim() && !/\d/.test(value) && !value.includes("\t"));
        if (!name && firstLine) setName(firstLine.trim().slice(0, 120));
        if (!project && firstLine) setProject(firstLine.trim().slice(0, 120));
      },
    });
  }

  function chooseRecipe(next: number) {
    if (!result) return;
    setColumn(next);
    setRows((current) =>
      toRows(result, next).map((row, index) => ({
        ...row,
        include: current[index]?.include ?? row.include,
        material: current[index]?.material ?? row.material,
        confirmed: current[index]?.confirmed ?? row.confirmed,
      })),
    );
  }

  function update(index: number, patch: Partial<Row>) {
    setRows((current) => current.map((row, position) => (position === index ? { ...row, ...patch } : row)));
  }

  const multi = (result?.amount_columns.length ?? 0) > 1;
  const all = multi && scope === "all" && target === "new";
  function unused(row: Row): boolean {
    if (!result) return false;
    return all ? row.line.amounts.every((value) => value === null) : recipeAmount(result, row.line, column) === null;
  }
  const indexed = rows.map((row, index) => ({ row, index }));
  const listed = indexed.filter(({ row }) => !unused(row));
  const toCheck = listed.filter(({ row }) => row.line.review.length > 0);
  const ready = listed.filter(({ row }) => row.line.review.length === 0);
  const notUsed = indexed.filter(({ row }) => unused(row));
  const included = listed.filter(({ row }) => row.include);
  const unconfirmed = included.filter(({ row }) => !row.confirmed);
  const incomplete = included.filter(({ row }) => !row.material || (!all && (!row.amount || !row.unit)));
  const onOrder = included.filter(({ row }) => row.material?.stock === "on_order").length;
  const unowned = included.filter(
    ({ row }) => row.material && !row.material.owned && row.material.stock !== "on_order",
  ).length;

  function lineInput(row: Row, amount: string | null, unit: LineUnit | null): LineInput {
    return {
      material_id: row.material?.id,
      amount: amount ?? "0",
      unit: unit ?? "parts",
      basis: row.line.active_fraction ? row.line.basis : "neat",
      active_fraction: row.line.active_fraction ?? "1",
      carrier_material_id: row.line.carrier?.id ?? null,
      notes: row.line.note,
    };
  }

  function validate(): boolean {
    if (!included.length) {
      setError("Tick at least one line to import");
      return false;
    }
    if (unconfirmed.length) {
      setError(
        `Confirm or change the material of ${plural(unconfirmed.length, "line")} to check, or untick ${unconfirmed.length === 1 ? "it" : "them"}`,
      );
      return false;
    }
    if (incomplete.length) {
      setError(
        `Choose a material${all ? "" : ", amount and unit"} for ${plural(incomplete.length, "ticked line")}, or untick ${incomplete.length === 1 ? "it" : "them"}`,
      );
      return false;
    }
    if (target === "new" && !all && !name.trim()) {
      setError("Name the new formula");
      return false;
    }
    return true;
  }

  function finish(message: string, leftOut: number) {
    toast.success(message, {
      description: leftOut ? `${plural(leftOut, "line")} left out are listed in the version notes.` : undefined,
    });
    onOpenChange(false);
    reset();
  }

  function submit() {
    if (!result || !validate()) return;
    setError(null);
    if (all) {
      const columns = result.amount_columns;
      const recipes = columns.map((recipe) => {
        const amountIn = (row: Row) => row.line.amounts[recipe.index] ?? null;
        return {
          name: project.trim() ? `${project.trim()} – ${recipe.label}` : recipe.label,
          lines: included
            .filter(({ row }) => amountIn(row) !== null)
            .map(({ row }) => lineInput(row, amountIn(row), recipe.unit ?? row.unit)),
          skipped: listed.filter(({ row }) => !row.include && amountIn(row) !== null).map(({ row }) => row.line.raw),
        };
      });
      importRecipes.mutate(
        { recipes: recipes.filter((recipe) => recipe.lines.length), project: project.trim(), notes: "Pasted recipe" },
        {
          onSuccess: (created) => {
            finish(`${plural(created.length, "formula")} created from the paste`, 0);
            if (created[0]) onImported(created[0]);
          },
        },
      );
      return;
    }
    const lines = included.map(({ row }) => lineInput(row, row.amount, row.unit));
    const skipped = listed.filter(({ row }) => !row.include).map(({ row }) => row.line.raw);
    importParsed.mutate(
      target === "replace" && replaceVersion
        ? { target: "replace", version_id: replaceVersion.id, lines, skipped }
        : { target: "new", name: name.trim(), lines, skipped, notes: "Pasted formula" },
      {
        onSuccess: (created) => {
          finish(
            target === "replace" ? `Lines replaced in ${created.label}` : "Formula created from the paste",
            skipped.length,
          );
          onImported(created);
        },
      },
    );
  }

  const pending = importParsed.isPending || importRecipes.isPending;
  const submitLabel = all
    ? `Create ${plural(result?.amount_columns.length ?? 0, "formula")}`
    : target === "replace" && replaceVersion
      ? `Replace with ${plural(included.length, "line")}`
      : `Create formula with ${plural(included.length, "line")}`;
  const footer = result ? (
    <>
      <Button
        variant="ghost"
        onClick={() => {
          setResult(null);
          setRows([]);
          setError(null);
        }}
      >
        Back
      </Button>
      <DialogClose />
      <Button variant="primary" onClick={submit} pending={pending} pendingLabel="Importing…">
        {submitLabel}
      </Button>
    </>
  ) : (
    <>
      <DialogClose />
      <Button variant="primary" type="submit" form="paste-formula" pending={parse.isPending} pendingLabel="Reading…">
        Read formula
      </Button>
    </>
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) reset();
      }}
      size="lg"
      title="Paste a formula"
      description="Lines like “Hedione 20”, “0.5 g Calone 10% in DPG”, a Markdown table, or cells copied from a spreadsheet (several recipes side by side too). Amounts of diluted lines are read as weighed."
      footer={footer}
    >
      {!result ? (
        <form id="paste-formula" className={styles.form} onSubmit={read} noValidate>
          <TextArea
            label="Formula text"
            mono
            rows={12}
            value={text}
            onValueChange={(value) => {
              setText(value);
              setError(null);
            }}
            placeholder={SAMPLE}
            submitOnModEnter
            error={error}
          />
          {parse.error ? (
            <InlineAlert tone="danger" title="Couldn’t read the formula">
              {describeError(parse.error)}
            </InlineAlert>
          ) : null}
        </form>
      ) : (
        <div className={styles.form}>
          <div className={styles.summary} aria-live="polite">
            <span>{plural(rows.length, "line")} read</span>
            {ready.length ? (
              <Badge tone="success" icon="check">
                {ready.length} ready
              </Badge>
            ) : null}
            {toCheck.length ? (
              <Badge tone="warning" icon="helpCircle">
                {toCheck.length} to check
              </Badge>
            ) : null}
            {notUsed.length ? <Badge dashed>{notUsed.length} not used</Badge> : null}
            {onOrder ? (
              <Badge tone="info" icon="truck">
                {onOrder} on order
              </Badge>
            ) : null}
            {unowned ? (
              <Badge tone="info" icon="cart">
                {unowned} not in stock
              </Badge>
            ) : null}
          </div>
          {result.notes.length ? <p className={styles.note}>{result.notes.join("; ")}.</p> : null}
          {result.warnings.length ? (
            <InlineAlert tone="warning" title="Check these before importing">
              <ul className={styles.note}>
                {result.warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </InlineAlert>
          ) : null}
          {multi ? (
            <div className={styles.row}>
              <Select<string>
                label="Recipe"
                options={result.amount_columns.map((recipe) => ({
                  value: String(recipe.index),
                  label: recipe.label,
                  detail: plural(recipe.used, "line"),
                }))}
                value={String(column)}
                onValueChange={(value) => value !== null && chooseRecipe(Number(value))}
              />
              {target === "new" ? (
                <RadioGroup<"one" | "all">
                  label="Import"
                  value={scope}
                  onValueChange={setScope}
                  options={[
                    { value: "one", label: "This recipe" },
                    {
                      value: "all",
                      label: `All ${result.amount_columns.length} recipes`,
                      description: "One formula each, in one project",
                    },
                  ]}
                />
              ) : null}
            </div>
          ) : null}
          {toCheck.length ? (
            <section className={styles.form} aria-label="Lines to check">
              <h3 className={styles.sectionTitle}>Check {toCheck.length === 1 ? "this line" : "these lines"}</h3>
              <LinesTable caption="Lines to check" rows={toCheck} showAmounts={!all} onUpdate={update} confirmable />
            </section>
          ) : null}
          {ready.length ? (
            <section className={styles.form} aria-label="Ready lines">
              <div className={styles.sectionHead}>
                <h3 className={styles.sectionTitle}>
                  {toCheck.length
                    ? `${plural(ready.length, "line")} ready`
                    : `All ${plural(ready.length, "line")} matched`}
                </h3>
                <Button
                  size="sm"
                  variant="ghost"
                  iconEnd={showReady ? "chevronUp" : "chevronDown"}
                  aria-expanded={showReady}
                  onClick={() => setShowReady((value) => !value)}
                >
                  {showReady ? "Hide" : "Review"}
                </Button>
              </div>
              {showReady ? (
                <LinesTable caption="Ready lines" rows={ready} showAmounts={!all} onUpdate={update} />
              ) : null}
            </section>
          ) : null}
          {notUsed.length ? (
            <section className={styles.form} aria-label="Lines not used">
              <div className={styles.sectionHead}>
                <h3 className={styles.sectionTitle}>
                  {plural(notUsed.length, "line")} without an amount {all ? "in any recipe" : "in this recipe"}
                </h3>
                <Button
                  size="sm"
                  variant="ghost"
                  iconEnd={showUnused ? "chevronUp" : "chevronDown"}
                  aria-expanded={showUnused}
                  onClick={() => setShowUnused((value) => !value)}
                >
                  {showUnused ? "Hide" : "Show"}
                </Button>
              </div>
              {showUnused ? (
                <ul className={styles.unused}>
                  {notUsed.map(({ row, index }) => (
                    <li key={`${row.line.line_no}-${index}`}>{row.line.name}</li>
                  ))}
                </ul>
              ) : null}
            </section>
          ) : null}
          {replaceVersion ? (
            <RadioGroup<"new" | "replace">
              label="Import into"
              orientation="horizontal"
              value={target}
              onValueChange={setTarget}
              options={[
                { value: "new", label: "A new formula" },
                { value: "replace", label: `Replace the lines of ${replaceVersion.name}` },
              ]}
            />
          ) : null}
          {all ? (
            <TextField
              label="Project"
              description="Each recipe becomes a formula named after the project and its column."
              value={project}
              onValueChange={setProject}
              autoComplete="off"
            />
          ) : target === "new" ? (
            <TextField label="Formula name" value={name} onValueChange={setName} autoComplete="off" />
          ) : null}
          {error ? <InlineAlert tone="danger" title={error} /> : null}
          {importParsed.error || importRecipes.error ? (
            <InlineAlert tone="danger" title="Couldn’t import the formula">
              {describeError(importParsed.error ?? importRecipes.error)}
            </InlineAlert>
          ) : null}
        </div>
      )}
    </Dialog>
  );
}

interface LinesTableProps {
  caption: string;
  rows: { row: Row; index: number }[];
  showAmounts: boolean;
  onUpdate: (index: number, patch: Partial<Row>) => void;
  /** Lines to check: say why, and offer to confirm the suggested material. */
  confirmable?: boolean;
}

function LinesTable({ caption, rows, showAmounts, onUpdate, confirmable = false }: LinesTableProps) {
  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <caption className={styles.subtle}>{caption}</caption>
        <thead>
          <tr>
            <th scope="col">Import</th>
            <th scope="col">Material</th>
            {showAmounts ? (
              <th scope="col" className={styles.num}>
                Amount
              </th>
            ) : null}
            <th scope="col">Dilution</th>
            <th scope="col">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ row, index }) => (
            <tr key={`${row.line.line_no}-${index}`}>
              <td>
                <Checkbox
                  label={<span className={styles.raw}>{row.line.raw}</span>}
                  checked={row.include}
                  onCheckedChange={(checked) => onUpdate(index, { include: checked })}
                />
              </td>
              <td>
                <div className={styles.materialCell}>
                  <Combobox<MaterialOption>
                    label={`Material for “${row.line.name}”`}
                    hideLabel
                    size="sm"
                    value={row.material}
                    onValueChange={(material) =>
                      onUpdate(index, {
                        material,
                        include: material ? true : row.include,
                        confirmed: material ? true : row.confirmed,
                      })
                    }
                    loadOptions={searchMaterials}
                    queryKey={["formulas", "material-options"]}
                    getOptionKey={(option) => option.id}
                    getOptionLabel={materialLabel}
                    renderOption={(option) => (
                      <span>
                        {materialLabel(option)}
                        {option.owned ? ", in stock" : ""}
                      </span>
                    )}
                    placeholder={row.line.name}
                  />
                  {row.line.status !== "matched" && row.line.candidates.length ? (
                    <span className={styles.chips} aria-label="Suggestions">
                      {row.line.candidates.slice(0, 3).map((candidate) => (
                        <button
                          key={candidate.id}
                          type="button"
                          className={styles.chip}
                          onClick={() => onUpdate(index, { material: candidate, include: true, confirmed: true })}
                        >
                          {candidate.name}
                          {candidate.owned ? ", in stock" : ""}
                        </button>
                      ))}
                    </span>
                  ) : null}
                </div>
              </td>
              {showAmounts ? (
                <td>
                  <div className={styles.amountCell}>
                    <NumberField
                      label={`Amount for ${row.line.name}`}
                      hideLabel
                      size="sm"
                      min={0}
                      value={row.amount}
                      onValueChange={(amount) => onUpdate(index, { amount })}
                      className={styles.amountField}
                    />
                    <Select<LineUnit>
                      label={`Unit for ${row.line.name}`}
                      hideLabel
                      size="sm"
                      options={UNIT_OPTIONS}
                      value={row.unit}
                      placeholder="Unit"
                      onValueChange={(unit) => onUpdate(index, { unit })}
                      className={styles.unitField}
                    />
                  </div>
                </td>
              ) : null}
              <td className={styles.muted}>
                {row.line.active_fraction
                  ? dilutionText(row.line.active_fraction, row.line.carrier) +
                    (row.line.carrier ? "" : row.line.carrier_text ? ` (${row.line.carrier_text}?)` : "")
                  : "Neat"}
              </td>
              <td>
                <LineStatus
                  row={row}
                  confirmable={confirmable}
                  onConfirm={() => onUpdate(index, { confirmed: true, include: true })}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LineStatus({ row, confirmable, onConfirm }: { row: Row; confirmable: boolean; onConfirm: () => void }) {
  const { line, material } = row;
  const sameAsMatch = material && material.id === line.match?.id;
  return (
    <div className={styles.statusCell}>
      <span className={styles.badges}>
        {!material ? (
          line.status === "unknown" ? (
            <Badge dashed icon="helpCircle">
              Unknown
            </Badge>
          ) : (
            <Badge tone="warning" icon="helpCircle">
              Check
            </Badge>
          )
        ) : row.confirmed ? (
          line.status === "matched" && sameAsMatch && line.review.length === 0 ? (
            <Badge tone="success" icon="check">
              Matched
            </Badge>
          ) : (
            <Badge tone="accent" icon="check">
              {sameAsMatch ? "Confirmed" : "Chosen"}
            </Badge>
          )
        ) : (
          <Badge tone="warning" icon="helpCircle">
            Check
          </Badge>
        )}
        {material?.stock === "on_order" ? (
          <Badge tone="info" icon="truck">
            On order
          </Badge>
        ) : material && !material.owned ? (
          <Badge tone="info" icon="cart">
            Not in stock
          </Badge>
        ) : null}
      </span>
      {line.settled_by && sameAsMatch ? (
        <div className={styles.subtle}>
          The one {line.settled_by === "in_stock" ? "in stock" : "on order"}, among materials of this name
        </div>
      ) : null}
      {confirmable && !row.confirmed
        ? line.review.map((reason) => (
            <div key={reason} className={styles.warning}>
              {reason}
            </div>
          ))
        : null}
      {confirmable && !row.confirmed && material ? (
        <Button size="sm" icon="check" onClick={onConfirm}>
          Confirm {material.name}
        </Button>
      ) : null}
      {line.match?.score && line.status !== "matched" && !row.confirmed ? (
        <div className={styles.subtle}>
          Confidence {formatPercent(line.match.score, { from: "fraction", decimals: 0 })}
        </div>
      ) : null}
    </div>
  );
}
